import React, {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'components';
import { Input, DropdownV2, Checkbox } from 'components/form';
import ButtonV3 from 'components/ComponentV2/ButtonV3';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import { QuestionState } from 'lib/models/answers';
import styles from './CustomQuestionModal.module.scss';

export const CustomQuestionModal = ({
  isOpen,
  onClose,
  style,
  onAddQuestion,
  questionTypes,
  selectedQuestion,
}: {
  isOpen: boolean;
  onClose: () => void;
  style?: Record<string, string>;
  onAddQuestion: (
    e: React.MouseEvent<HTMLButtonElement>,
    question: {
      optionsList: Array<string>;
      isRequired: boolean;
      questionType: string;
      title: string;
      isEdit: boolean;
    }
  ) => void;
  questionTypes: Record<string, { label: string; choice: number }>;
  selectedQuestion: {
    id?: string;
    title?: string;
    state?: QuestionState;
    isNew?: boolean;
    options?: Array<string>;
    question_type: string;
    isEdit?: boolean;
  } | null;
}): ReactElement => {
  const [questionType, setQuestionType] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [optionsList, setOptionsList] = useState<Array<string>>([]);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const questionOptions = Object.keys(questionTypes)?.map((key, index) => ({
    id: `${index}`,
    label: questionTypes[key]?.label,
    value: key,
  }));
  const [t] = useTranslation('opportunity.posting');

  useEffect(() => {
    // Scroll to the bottom of the content area on mount and whenever new items are added
    contentRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [optionsList]);

  useEffect(() => {
    if (selectedQuestion) {
      const { title, state, options, question_type } = selectedQuestion;
      setQuestionType(question_type);
      setTitle(title || '');
      setIsRequired(state === QuestionState.REQUIRED);
      setOptionsList(options || []);
    }
  }, [selectedQuestion]);

  const handleChange = (index: number, value: string) => {
    setOptionsList((list) =>
      list.map((item, itemIndex) => {
        if (index == itemIndex) {
          return value;
        }
        return item;
      })
    );
  };

  const questionTypeLabel = useMemo(() => {
    const selectedQuestion = questionOptions.find(
      (option) => option.value === questionType
    );
    return selectedQuestion?.label ?? '';
  }, [questionType]);

  const isValidOptions = optionsList.some((option) => !option);
  const isChoiceOption = ['single_choice', 'multiple_choice'].includes(
    questionType
  );
  const showOptionsLengthTooltip = isChoiceOption && optionsList.length < 2;
  const showEmptyTooltip =
    !title || !questionType || (isChoiceOption && isValidOptions);
  const tooltipContent = showOptionsLengthTooltip
    ? t('event-posting.custom-question-modal-tooltip-1')
    : showEmptyTooltip
    ? t('event-posting.custom-question-modal-tooltip-2')
    : '';
  const isDisabled = showEmptyTooltip || showOptionsLengthTooltip;
  return (
    <>
      <Modal
        contentClassName={styles.modalContent}
        isOpen={isOpen}
        onRequestClose={onClose}
        isFullScreen={false}
      >
        <div
          className={styles.CustomQuestionModal}
          style={style}
          data-testid="CustomQuestionModal"
        >
          <div className={styles.CustomQuestionModalHeader}>
            <p className={styles.CustomQuestionModalHeaderTitle}>
              {selectedQuestion
                ? t('event-posting.custom-question-modal-title-edit')
                : t('event-posting.custom-question-modal-title')}
            </p>
            <IconButton
              size="small"
              iconName="close"
              className={styles.cancelIcon}
              onClick={onClose}
            />
          </div>
          <div
            className={cn(styles.CustomQuestionModalContent, {
              [styles.CustomQuestionModalContentOverflow]:
                optionsList?.length >= 3,
            })}
          >
            <Input
              label={t('event-posting.question')}
              placeholder={t('event-posting.question-placeholder')}
              className={styles.questionInput}
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              labelClassName={styles.questionInputLabel}
              maxLength={120}
            />
            <DropdownV2
              label={t('event-posting.select-question-type')}
              title={t('event-posting.question-type')}
              value={questionType}
              onChange={(value) => setQuestionType(value)}
              options={questionOptions}
              selectedValueLabel={questionTypeLabel}
              className={styles.CustomQuestionModalDropdown}
              triggerButtonClassName={
                styles.CustomQuestionModalDropdownTriggerButton
              }
              isValid={true}
              labelStyle={{
                fontSize: '15px',
                lineHeight: '22px',
              }}
            />
            {['single_choice', 'multiple_choice'].includes(questionType) ? (
              <div className={styles.CustomQuestionModalOptionsBox}>
                {optionsList.map((label, index) => (
                  <>
                    <div
                      className={
                        styles.CustomQuestionModalOptionsBoxInputOption
                      }
                    >
                      <p>{index + 1}</p>
                      <input
                        value={label}
                        placeholder="Option"
                        onChange={(e) => handleChange(index, e?.target?.value)}
                        type="text"
                        autoFocus
                        maxLength={100}
                        onKeyPress={(e) => {
                          e.key === 'Enter' &&
                            setOptionsList([...optionsList, '']);
                        }}
                      />
                      <IconButton
                        type="button"
                        iconName="cancel"
                        onClick={() => {
                          setOptionsList((list) =>
                            list.filter((_, ind) => index !== ind)
                          );
                        }}
                        className={
                          styles.CustomQuestionModalOptionsBoxInputOptionRemoveButton
                        }
                      />
                    </div>
                    <div
                      className={styles.CustomQuestionModalOptionsBoxSeparator}
                    />
                  </>
                ))}
                <div
                  className={styles.CustomQuestionModalOptionsBoxAddOption}
                  onClick={() => {
                    setOptionsList([...optionsList, '']);
                  }}
                >
                  <p>+</p>
                  <p>{t('event-posting.add-option')}</p>
                </div>
              </div>
            ) : null}
            <Checkbox
              checked={isRequired}
              onChange={() => setIsRequired(!isRequired)}
              className={styles.CustomQuestionModalCheckbox}
            >
              <p className={styles.CustomQuestionModalCheckboxLabel}>
                {t('event-posting.required')}
              </p>
            </Checkbox>
            <div ref={contentRef}></div>
          </div>
          <div className={styles.CustomQuestionModalFooter}>
            <Tooltip
              content={tooltipContent}
              position="top"
              color="dark"
              disabled={!tooltipContent}
              className={styles.CustomQuestionModalFooterTooltip}
              containerClassName={
                styles.CustomQuestionModalFooterTooltipContainer
              }
            >
              <ButtonV3
                color="primary"
                className={styles.CustomQuestionModalFooterButton}
                onClick={(e) => {
                  if (!isDisabled) {
                    onAddQuestion(e, {
                      optionsList,
                      title,
                      questionType,
                      isRequired,
                      isEdit: selectedQuestion ? true : false,
                    });
                  }
                }}
              >
                {selectedQuestion
                  ? t('event-posting.done')
                  : t('event-posting.add-question')}
              </ButtonV3>
            </Tooltip>
          </div>
        </div>
      </Modal>
    </>
  );
};
